/**
 * useLeafletDraw Hook
 * Manages Leaflet.Draw functionality for map editing
 *
 * Features:
 * - Drawing polygons, polylines, rectangles, circles
 * - Editing existing shapes
 * - Deleting shapes
 * - Converting shapes to GeoJSON
 * - Layer-based organization
 * - Crosshair cursor when drawing
 * - Keyboard shortcuts (Enter to finish, Escape to cancel)
 */
import { useCallback, useRef, useState, useEffect } from 'react'
import L from 'leaflet'
import 'leaflet-draw'
import 'leaflet-draw/dist/leaflet.draw.css'

// CSS for crosshair cursor when drawing and snap indicator
const DRAWING_CURSOR_STYLE = `
  .leaflet-container.drawing-mode {
    cursor: crosshair !important;
  }
  .leaflet-container.drawing-mode .leaflet-interactive {
    cursor: crosshair !important;
  }
  .leaflet-container.drawing-mode .leaflet-marker-icon {
    cursor: crosshair !important;
  }
  /* Snap indicator - highlight first point when can close */
  .leaflet-draw-marker.snap-target {
    filter: drop-shadow(0 0 8px #10B981) drop-shadow(0 0 16px #10B981);
    transform: scale(1.3);
    transition: all 0.15s ease;
  }
  .leaflet-draw-marker.snap-ready {
    background: #10B981 !important;
    border-color: #059669 !important;
  }
  /* Close indicator circle around first point */
  .snap-close-indicator {
    position: absolute;
    width: 30px;
    height: 30px;
    border: 3px dashed #10B981;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    animation: pulse-snap 1s ease-in-out infinite;
    z-index: 1000;
  }
  @keyframes pulse-snap {
    0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
    50% { opacity: 1; transform: translate(-50%, -50%) scale(1.2); }
  }
`

// Inject styles once
let stylesInjected = false
function injectDrawingStyles() {
  if (stylesInjected) return
  const style = document.createElement('style')
  style.textContent = DRAWING_CURSOR_STYLE
  document.head.appendChild(style)
  stylesInjected = true
}

// Default layer type colors
export const LAYER_TYPE_COLORS = {
  dat_nen: { stroke: '#D97706', fill: '#FCD34D' },       // amber - đất nền
  khu_thuong_mai: { stroke: '#7C3AED', fill: '#C4B5FD' }, // violet - thương mại
  cong_vien: { stroke: '#059669', fill: '#6EE7B7' },      // emerald - công viên
  giao_thong: { stroke: '#6B7280', fill: '#D1D5DB' },     // gray - giao thông
  tien_ich: { stroke: '#2563EB', fill: '#93C5FD' },       // blue - tiện ích
  custom: { stroke: '#3B82F6', fill: '#93C5FD' },         // blue - custom
}

// Stroke style patterns
export const STROKE_STYLES = {
  solid: null,
  dashed: '10, 10',
  dotted: '2, 8',
}

/**
 * Convert Leaflet layer to GeoJSON feature
 */
function layerToGeoJSON(layer, shapeType) {
  const geoJSON = layer.toGeoJSON()

  // For circles, store center and radius separately
  if (shapeType === 'circle' && layer.getRadius) {
    const center = layer.getLatLng()
    geoJSON.properties = {
      ...geoJSON.properties,
      radius: layer.getRadius(),
      center: [center.lat, center.lng],
    }
  }

  return geoJSON
}

/**
 * Get shape type from Leaflet layer
 */
function getShapeType(layer) {
  if (layer instanceof L.Circle) return 'circle'
  if (layer instanceof L.Rectangle) return 'rectangle'
  if (layer instanceof L.Polygon) return 'polygon'
  if (layer instanceof L.Polyline) return 'polyline'
  return 'polygon'
}

export default function useLeafletDraw({
  map,
  onShapeCreated,
  onShapeEdited,
  onShapeDeleted,
  onShapeSelected,
  defaultStyle = {},
}) {
  const [drawingMode, setDrawingMode] = useState(null) // null, 'polygon', 'polyline', 'rectangle', 'circle', 'edit', 'delete'
  const [selectedShape, setSelectedShape] = useState(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [pointCount, setPointCount] = useState(0) // Track number of points drawn
  const [canSnapClose, setCanSnapClose] = useState(false) // Whether cursor is near first point

  const drawControlRef = useRef(null)
  const drawnItemsRef = useRef(null)
  const currentDrawerRef = useRef(null)
  const shapeLayerMapRef = useRef(new Map()) // Maps shape IDs to Leaflet layers
  const snapIndicatorRef = useRef(null) // Visual indicator for snap-to-close

  // Inject drawing styles on mount
  useEffect(() => {
    injectDrawingStyles()
  }, [])

  // Toggle crosshair cursor when drawing
  useEffect(() => {
    if (!map) return
    const container = map.getContainer()
    if (isDrawing) {
      container.classList.add('drawing-mode')
    } else {
      container.classList.remove('drawing-mode')
    }
  }, [map, isDrawing])

  // Keyboard shortcuts for drawing
  useEffect(() => {
    if (!map) return

    const handleKeyDown = (e) => {
      // Escape - cancel drawing
      if (e.key === 'Escape' && isDrawing) {
        cancelDrawing()
        return
      }

      // Enter - finish drawing (for polygon/polyline)
      if (e.key === 'Enter' && isDrawing && currentDrawerRef.current) {
        const drawer = currentDrawerRef.current
        // Complete the shape if possible
        if (drawer.completeShape) {
          drawer.completeShape()
        } else if (drawer._markers && drawer._markers.length >= 2) {
          // For polyline, need at least 2 points
          drawer._finishShape()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [map, isDrawing])

  // Initialize drawnItems layer group
  useEffect(() => {
    if (!map) return

    // Create feature group for drawn items
    const drawnItems = new L.FeatureGroup()
    map.addLayer(drawnItems)
    drawnItemsRef.current = drawnItems

    // Listen for draw events
    map.on(L.Draw.Event.CREATED, handleDrawCreated)
    map.on(L.Draw.Event.EDITED, handleDrawEdited)
    map.on(L.Draw.Event.DELETED, handleDrawDeleted)

    return () => {
      map.off(L.Draw.Event.CREATED, handleDrawCreated)
      map.off(L.Draw.Event.EDITED, handleDrawEdited)
      map.off(L.Draw.Event.DELETED, handleDrawDeleted)

      if (drawnItemsRef.current) {
        map.removeLayer(drawnItemsRef.current)
      }

      // Remove drawing cursor on cleanup
      const container = map.getContainer()
      container.classList.remove('drawing-mode')
    }
  }, [map])

  // Handle draw:created event
  const handleDrawCreated = useCallback((e) => {
    const { layer, layerType } = e

    // Cleanup vertex listener if exists
    if (currentDrawerRef.current && currentDrawerRef.current._cleanupVertexListener) {
      currentDrawerRef.current._cleanupVertexListener()
    }

    // Cleanup snap indicator
    if (snapIndicatorRef.current) {
      snapIndicatorRef.current.style.display = 'none'
    }

    // Add to drawnItems
    if (drawnItemsRef.current) {
      drawnItemsRef.current.addLayer(layer)
    }

    // Add click handler for selection
    layer.on('click', () => {
      selectShape(layer)
    })

    // Convert to GeoJSON and notify parent
    const shapeType = getShapeType(layer)
    const geoJSON = layerToGeoJSON(layer, shapeType)

    setIsDrawing(false)
    setDrawingMode(null)
    setPointCount(0)
    setCanSnapClose(false)
    currentDrawerRef.current = null

    if (onShapeCreated) {
      onShapeCreated({
        layer,
        shapeType,
        geoJSON,
        style: getLayerStyle(layer),
      })
    }
  }, [onShapeCreated])

  // Handle draw:edited event
  const handleDrawEdited = useCallback((e) => {
    const layers = e.layers
    const editedShapes = []

    layers.eachLayer((layer) => {
      const shapeType = getShapeType(layer)
      const geoJSON = layerToGeoJSON(layer, shapeType)

      // Find shape ID from our map
      let shapeId = null
      shapeLayerMapRef.current.forEach((l, id) => {
        if (l === layer) shapeId = id
      })

      editedShapes.push({
        id: shapeId,
        layer,
        shapeType,
        geoJSON,
      })
    })

    if (onShapeEdited && editedShapes.length > 0) {
      onShapeEdited(editedShapes)
    }
  }, [onShapeEdited])

  // Handle draw:deleted event
  const handleDrawDeleted = useCallback((e) => {
    const layers = e.layers
    const deletedIds = []

    layers.eachLayer((layer) => {
      shapeLayerMapRef.current.forEach((l, id) => {
        if (l === layer) {
          deletedIds.push(id)
          shapeLayerMapRef.current.delete(id)
        }
      })
    })

    if (onShapeDeleted && deletedIds.length > 0) {
      onShapeDeleted(deletedIds)
    }
  }, [onShapeDeleted])

  // Get layer style
  const getLayerStyle = (layer) => {
    return {
      color: layer.options.color,
      fillColor: layer.options.fillColor,
      weight: layer.options.weight,
      opacity: layer.options.opacity,
      fillOpacity: layer.options.fillOpacity,
      dashArray: layer.options.dashArray,
    }
  }

  // Select a shape
  const selectShape = useCallback((layer) => {
    // Deselect previous
    if (selectedShape) {
      selectedShape.setStyle({
        ...selectedShape.options,
        weight: selectedShape.options._originalWeight || 3,
      })
    }

    // Select new
    if (layer) {
      layer.options._originalWeight = layer.options.weight
      layer.setStyle({ weight: layer.options.weight + 2 })

      // Find shape ID
      let shapeId = null
      shapeLayerMapRef.current.forEach((l, id) => {
        if (l === layer) shapeId = id
      })

      setSelectedShape(layer)

      if (onShapeSelected) {
        onShapeSelected({
          id: shapeId,
          layer,
          shapeType: getShapeType(layer),
          geoJSON: layerToGeoJSON(layer, getShapeType(layer)),
          style: getLayerStyle(layer),
        })
      }
    } else {
      setSelectedShape(null)
      if (onShapeSelected) {
        onShapeSelected(null)
      }
    }
  }, [selectedShape, onShapeSelected])

  // Start drawing
  const startDrawing = useCallback((type, options = {}) => {
    if (!map) return

    // Cancel any existing drawer
    cancelDrawing()

    const style = {
      color: options.strokeColor || defaultStyle.strokeColor || '#3388ff',
      fillColor: options.fillColor || defaultStyle.fillColor || '#3388ff',
      weight: options.strokeWidth || defaultStyle.strokeWidth || 3,
      opacity: options.strokeOpacity || defaultStyle.strokeOpacity || 1,
      fillOpacity: options.fillOpacity || defaultStyle.fillOpacity || 0.2,
      dashArray: STROKE_STYLES[options.strokeStyle] || STROKE_STYLES[defaultStyle.strokeStyle] || null,
    }

    let drawer
    switch (type) {
      case 'polygon':
        drawer = new L.Draw.Polygon(map, {
          shapeOptions: style,
          allowIntersection: false,
          showArea: true,
          repeatMode: false,
          // Don't auto-complete - let user click first point or double-click
          guidelineDistance: 10,
        })
        break
      case 'polyline':
        drawer = new L.Draw.Polyline(map, {
          shapeOptions: style,
          repeatMode: false,
          guidelineDistance: 10,
        })
        break
      case 'rectangle':
        drawer = new L.Draw.Rectangle(map, {
          shapeOptions: style,
          repeatMode: false,
        })
        break
      case 'circle':
        drawer = new L.Draw.Circle(map, {
          shapeOptions: style,
          showRadius: true,
          metric: true,
          repeatMode: false,
        })
        break
      default:
        return
    }

    currentDrawerRef.current = drawer
    drawer.enable()
    setDrawingMode(type)
    setIsDrawing(true)
    setPointCount(0)

    // Helper: create/update snap indicator around first point
    const updateSnapIndicator = (show, firstLatLng) => {
      if (show && firstLatLng) {
        const point = map.latLngToContainerPoint(firstLatLng)
        const container = map.getContainer()

        if (!snapIndicatorRef.current) {
          const indicator = document.createElement('div')
          indicator.className = 'snap-close-indicator'
          container.appendChild(indicator)
          snapIndicatorRef.current = indicator
        }

        snapIndicatorRef.current.style.left = point.x + 'px'
        snapIndicatorRef.current.style.top = point.y + 'px'
        snapIndicatorRef.current.style.display = 'block'
      } else if (snapIndicatorRef.current) {
        snapIndicatorRef.current.style.display = 'none'
      }
    }

    // Helper: highlight first marker
    const highlightFirstMarker = (highlight, markers) => {
      if (markers && markers[0] && markers[0]._icon) {
        if (highlight) {
          markers[0]._icon.classList.add('snap-target')
        } else {
          markers[0]._icon.classList.remove('snap-target')
        }
      }
    }

    // Listen for vertex added to track point count
    const handleDrawVertex = () => {
      // Get marker count from the drawer directly
      if (currentDrawerRef.current && currentDrawerRef.current._markers) {
        const markers = currentDrawerRef.current._markers
        setPointCount(markers.length)

        // Show snap indicator when we have enough points
        const minPoints = type === 'polygon' ? 3 : 3
        if (markers.length >= minPoints && markers[0]) {
          updateSnapIndicator(true, markers[0].getLatLng())
        }
      }
    }

    // Listen for mouse move to detect hover near first point
    const handleMouseMove = (e) => {
      if (!currentDrawerRef.current || !currentDrawerRef.current._markers) return

      const markers = currentDrawerRef.current._markers
      const minPoints = type === 'polygon' ? 3 : 3

      if (markers.length < minPoints) {
        setCanSnapClose(false)
        highlightFirstMarker(false, markers)
        return
      }

      const firstMarker = markers[0]
      if (!firstMarker) return

      const firstLatLng = firstMarker.getLatLng()
      const firstPoint = map.latLngToContainerPoint(firstLatLng)
      const mousePoint = e.containerPoint

      const distance = firstPoint.distanceTo(mousePoint)
      const SNAP_DISTANCE = 20 // pixels

      if (distance < SNAP_DISTANCE) {
        setCanSnapClose(true)
        highlightFirstMarker(true, markers)
        // Update snap indicator position (in case map moved)
        updateSnapIndicator(true, firstLatLng)
      } else {
        setCanSnapClose(false)
        highlightFirstMarker(false, markers)
      }
    }

    // Listen for click - if near first point, close the shape as polygon
    const handleMapClick = (e) => {
      if (!currentDrawerRef.current || !currentDrawerRef.current._markers) return

      const markers = currentDrawerRef.current._markers
      const minPoints = 3 // Need at least 3 points to close

      if (markers.length < minPoints) return

      const firstMarker = markers[0]
      if (!firstMarker) return

      const firstLatLng = firstMarker.getLatLng()
      const firstPoint = map.latLngToContainerPoint(firstLatLng)
      const clickPoint = e.containerPoint

      const distance = firstPoint.distanceTo(clickPoint)
      const SNAP_DISTANCE = 20 // pixels

      if (distance < SNAP_DISTANCE) {
        // User clicked near first point - close the shape as POLYGON!
        e.originalEvent.preventDefault()
        e.originalEvent.stopPropagation()

        // Clean up indicator
        updateSnapIndicator(false)
        highlightFirstMarker(false, markers)

        // Get all latlngs from markers
        const latlngs = markers.map(m => m.getLatLng())

        // Cancel the current drawer
        if (currentDrawerRef.current) {
          currentDrawerRef.current.disable()
        }

        // Get style from current drawing
        const currentStyle = {
          color: style.color,
          fillColor: style.fillColor,
          weight: style.weight,
          opacity: style.opacity,
          fillOpacity: 0.3, // Add fill for polygon
        }

        // Create a proper polygon from the points
        const polygon = L.polygon(latlngs, currentStyle)

        // Add to drawn items
        if (drawnItemsRef.current) {
          drawnItemsRef.current.addLayer(polygon)
        }

        // Add click handler for selection
        polygon.on('click', () => {
          selectShape(polygon)
        })

        // Convert to GeoJSON
        const geoJSON = polygon.toGeoJSON()

        // Cleanup
        setIsDrawing(false)
        setDrawingMode(null)
        setPointCount(0)
        setCanSnapClose(false)
        currentDrawerRef.current = null

        // Cleanup listeners
        map.off('draw:drawvertex', handleDrawVertex)
        map.off('mousemove', handleMouseMove)
        map.off('click', handleMapClick)

        // Notify parent - this is a POLYGON (closed path)
        if (onShapeCreated) {
          onShapeCreated({
            layer: polygon,
            shapeType: 'polygon',
            geoJSON,
            style: currentStyle,
            isClosed: true,
          })
        }

        return // Don't continue with normal click
      }
    }

    map.on('draw:drawvertex', handleDrawVertex)
    map.on('mousemove', handleMouseMove)
    map.on('click', handleMapClick)

    // Store cleanup function
    drawer._cleanupVertexListener = () => {
      map.off('draw:drawvertex', handleDrawVertex)
      map.off('mousemove', handleMouseMove)
      map.off('click', handleMapClick)
      updateSnapIndicator(false)
      if (currentDrawerRef.current && currentDrawerRef.current._markers) {
        highlightFirstMarker(false, currentDrawerRef.current._markers)
      }
    }
  }, [map, defaultStyle])

  // Finish drawing (complete the shape)
  const finishDrawing = useCallback(() => {
    if (!currentDrawerRef.current) return

    const drawer = currentDrawerRef.current

    // For polygon - need at least 3 points
    if (drawingMode === 'polygon') {
      if (drawer._markers && drawer._markers.length >= 3) {
        drawer._finishShape()
      }
    }
    // For polyline - need at least 2 points
    else if (drawingMode === 'polyline') {
      if (drawer._markers && drawer._markers.length >= 2) {
        drawer._finishShape()
      }
    }
  }, [drawingMode])

  // Cancel drawing
  const cancelDrawing = useCallback(() => {
    if (currentDrawerRef.current) {
      // Cleanup vertex listener if exists
      if (currentDrawerRef.current._cleanupVertexListener) {
        currentDrawerRef.current._cleanupVertexListener()
      }
      currentDrawerRef.current.disable()
      currentDrawerRef.current = null
    }
    // Cleanup snap indicator
    if (snapIndicatorRef.current) {
      snapIndicatorRef.current.style.display = 'none'
    }
    setDrawingMode(null)
    setIsDrawing(false)
    setPointCount(0)
    setCanSnapClose(false)
  }, [])

  // Enable edit mode
  const enableEditMode = useCallback(() => {
    if (!map || !drawnItemsRef.current) return

    cancelDrawing()

    const editHandler = new L.EditToolbar.Edit(map, {
      featureGroup: drawnItemsRef.current,
    })
    editHandler.enable()
    currentDrawerRef.current = editHandler
    setDrawingMode('edit')
  }, [map, cancelDrawing])

  // Enable delete mode
  const enableDeleteMode = useCallback(() => {
    if (!map || !drawnItemsRef.current) return

    cancelDrawing()

    const deleteHandler = new L.EditToolbar.Delete(map, {
      featureGroup: drawnItemsRef.current,
    })
    deleteHandler.enable()
    currentDrawerRef.current = deleteHandler
    setDrawingMode('delete')
  }, [map, cancelDrawing])

  // Save edit/delete changes
  const saveChanges = useCallback(() => {
    if (currentDrawerRef.current && currentDrawerRef.current.save) {
      currentDrawerRef.current.save()
    }
    cancelDrawing()
  }, [cancelDrawing])

  // Revert edit/delete changes
  const revertChanges = useCallback(() => {
    if (currentDrawerRef.current && currentDrawerRef.current.revertLayers) {
      currentDrawerRef.current.revertLayers()
    }
    cancelDrawing()
  }, [cancelDrawing])

  // Add existing shape to map
  const addShape = useCallback((shapeData) => {
    if (!map || !drawnItemsRef.current) return null

    const { id, shapeType, geojson, style = {} } = shapeData

    let layer

    const layerStyle = {
      color: style.stroke_color || '#3388ff',
      fillColor: style.fill_color || '#3388ff',
      weight: style.stroke_width || 3,
      opacity: style.stroke_opacity || 1,
      fillOpacity: style.fill_opacity || 0.2,
      dashArray: STROKE_STYLES[style.stroke_style] || null,
    }

    // Handle different geometry types
    const geometry = geojson.geometry || geojson

    if (shapeType === 'circle') {
      // Circle needs special handling
      const props = geojson.properties || {}
      const center = props.center || geometry.coordinates
      const radius = props.radius || 100

      layer = L.circle(
        Array.isArray(center[0]) ? center : [center[1], center[0]],
        { ...layerStyle, radius }
      )
    } else if (shapeType === 'rectangle') {
      const coords = geometry.coordinates[0]
      const bounds = L.latLngBounds(
        coords.map(c => [c[1], c[0]])
      )
      layer = L.rectangle(bounds, layerStyle)
    } else if (shapeType === 'polygon') {
      layer = L.geoJSON(geojson, {
        style: layerStyle,
      }).getLayers()[0]
    } else if (shapeType === 'polyline') {
      layer = L.geoJSON(geojson, {
        style: layerStyle,
      }).getLayers()[0]
    }

    if (layer) {
      // Add click handler
      layer.on('click', () => {
        selectShape(layer)
      })

      drawnItemsRef.current.addLayer(layer)
      shapeLayerMapRef.current.set(id, layer)
    }

    return layer
  }, [map, selectShape])

  // Remove shape from map
  const removeShape = useCallback((shapeId) => {
    const layer = shapeLayerMapRef.current.get(shapeId)
    if (layer && drawnItemsRef.current) {
      drawnItemsRef.current.removeLayer(layer)
      shapeLayerMapRef.current.delete(shapeId)

      if (selectedShape === layer) {
        setSelectedShape(null)
      }
    }
  }, [selectedShape])

  // Update shape style
  const updateShapeStyle = useCallback((shapeId, style) => {
    const layer = shapeLayerMapRef.current.get(shapeId)
    if (layer) {
      layer.setStyle({
        color: style.stroke_color || layer.options.color,
        fillColor: style.fill_color || layer.options.fillColor,
        weight: style.stroke_width || layer.options.weight,
        opacity: style.stroke_opacity || layer.options.opacity,
        fillOpacity: style.fill_opacity || layer.options.fillOpacity,
        dashArray: STROKE_STYLES[style.stroke_style] || layer.options.dashArray,
      })
    }
  }, [])

  // Clear all shapes
  const clearAllShapes = useCallback(() => {
    if (drawnItemsRef.current) {
      drawnItemsRef.current.clearLayers()
      shapeLayerMapRef.current.clear()
      setSelectedShape(null)
    }
  }, [])

  // Set visibility for a shape
  const setShapeVisibility = useCallback((shapeId, visible) => {
    const layer = shapeLayerMapRef.current.get(shapeId)
    if (layer) {
      if (visible) {
        layer.setStyle({ opacity: layer.options._originalOpacity || 1, fillOpacity: layer.options._originalFillOpacity || 0.2 })
      } else {
        layer.options._originalOpacity = layer.options.opacity
        layer.options._originalFillOpacity = layer.options.fillOpacity
        layer.setStyle({ opacity: 0, fillOpacity: 0 })
      }
    }
  }, [])

  // Fit map to shape bounds
  const fitToShape = useCallback((shapeId) => {
    const layer = shapeLayerMapRef.current.get(shapeId)
    if (layer && map) {
      map.fitBounds(layer.getBounds(), { padding: [50, 50] })
    }
  }, [map])

  // Fit map to all shapes
  const fitToAllShapes = useCallback(() => {
    if (drawnItemsRef.current && map && drawnItemsRef.current.getLayers().length > 0) {
      map.fitBounds(drawnItemsRef.current.getBounds(), { padding: [50, 50] })
    }
  }, [map])

  return {
    // State
    drawingMode,
    selectedShape,
    isDrawing,
    pointCount, // Number of vertices drawn so far
    canSnapClose, // Whether cursor is near first point (can click to close)

    // Drawing actions
    startDrawing,
    finishDrawing, // Manually finish drawing polygon/polyline
    cancelDrawing,
    enableEditMode,
    enableDeleteMode,
    saveChanges,
    revertChanges,

    // Shape management
    addShape,
    removeShape,
    updateShapeStyle,
    clearAllShapes,
    selectShape: (id) => {
      const layer = shapeLayerMapRef.current.get(id)
      selectShape(layer || null)
    },
    deselectShape: () => selectShape(null),

    // Visibility
    setShapeVisibility,

    // Navigation
    fitToShape,
    fitToAllShapes,

    // Refs for advanced usage
    drawnItemsRef,
    shapeLayerMapRef,
  }
}
